import { Capacitor } from '@capacitor/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';

export type TTSGender = 'male' | 'female';

const isAndroid = (() => {
  try { return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'; }
  catch { return false; }
})();

const hasWebSpeech = !isAndroid && typeof window !== 'undefined' && 'speechSynthesis' in window;

const RATE = 0.85;

// ── Web voice detection & gender map ─────────────────────────────────────────
// Setting utt.lang to a language that is NOT installed on Windows causes the
// OS / browser to show a "download language pack" dialog. So we probe the
// available voices first: only use hi-IN if it is actually present.
let webHindiLang: string | null = null;

// Voice map: 5-char lang prefix → { male, female } best-match voices
const FEMALE_HINTS = ['zira', 'kalpana', 'female', 'woman', 'heemi', 'shruti', 'raveena', 'google', 'neerja', 'aditi'];
const MALE_HINTS   = ['david', 'hemant', 'male', 'man', 'rishi', 'mark', 'james', 'prabhat'];

const voiceMap: Record<string, { male: SpeechSynthesisVoice | null; female: SpeechSynthesisVoice | null }> = {};

if (hasWebSpeech) {
  const buildVoiceMap = () => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return;

    const byLang: Record<string, SpeechSynthesisVoice[]> = {};
    voices.forEach(v => {
      const key = v.lang.slice(0, 5).toLowerCase();
      (byLang[key] ??= []).push(v);
    });

    Object.entries(byLang).forEach(([key, vs]) => {
      const n = (v: SpeechSynthesisVoice) => v.name.toLowerCase();
      const female = vs.find(v => FEMALE_HINTS.some(h => n(v).includes(h))) ?? vs[0] ?? null;
      const male   = vs.find(v => MALE_HINTS.some(h => n(v).includes(h)))
                  ?? vs.find(v => v !== female)
                  ?? vs[0]
                  ?? null;
      voiceMap[key] = { female, male };
    });

    webHindiLang = voiceMap['hi-in']?.female?.lang
      ?? voices.find(v => v.lang.startsWith('hi'))?.lang
      ?? null;
  };
  window.speechSynthesis.addEventListener('voiceschanged', buildVoiceMap);
  buildVoiceMap();
}

// Gender audio parameters — pitch and rate are the primary differentiators
// when only one voice is installed for a language (common for Hindi on Windows/Android).
// Values chosen to be clearly audible: 0.5 sounds noticeably deep/male,
// 1.6 sounds noticeably high/female.
const GENDER_PARAMS: Record<'male' | 'female' | 'default', { pitch: number; rate: number }> = {
  male:    { pitch: 0.5, rate: 0.95 },
  female:  { pitch: 1.6, rate: 0.85 },
  default: { pitch: 1.0, rate: RATE  },
};

// ── Web-only queue ────────────────────────────────────────────────────────────
interface WebJob { text: string; voice?: SpeechSynthesisVoice | null; lang?: string | null; pitch: number; rate: number; }

const webQueue: WebJob[] = [];
let webBusy = false;

function webDequeue() {
  if (webBusy || !webQueue.length || !hasWebSpeech) return;
  webBusy = true;
  const job = webQueue.shift()!;
  const utt = new SpeechSynthesisUtterance(job.text);
  if (job.voice) {
    utt.voice = job.voice;
    utt.lang  = job.voice.lang;
  } else if (job.lang ?? webHindiLang) {
    utt.lang = (job.lang ?? webHindiLang)!;
  }
  utt.rate   = job.rate;
  utt.pitch  = job.pitch;
  utt.volume = 1.0;
  utt.onend  = utt.onerror = () => { webBusy = false; webDequeue(); };
  if (window.speechSynthesis.paused) window.speechSynthesis.resume();
  window.speechSynthesis.speak(utt);
}

// Internal helpers ─────────────────────────────────────────────────────────────

function speak(text: string, gender?: TTSGender, langOverride?: string) {
  const params = gender ? GENDER_PARAMS[gender] : GENDER_PARAMS.default;
  if (isAndroid) {
    TextToSpeech.speak({
      text, lang: langOverride ?? 'hi-IN',
      rate: params.rate, pitch: params.pitch, volume: 1.0, category: 'ambient',
    }).catch(() => {});
  } else if (hasWebSpeech) {
    const langKey = (langOverride ?? webHindiLang ?? 'hi-IN').slice(0, 5).toLowerCase();
    const voices  = voiceMap[langKey];
    // Prefer a voice matched by gender name; fall back to any available voice.
    // Pitch + rate are always applied so even a single-voice system sounds different.
    const voice = (gender && voices ? voices[gender] : null) ?? null;
    webQueue.push({ text, voice, lang: langOverride ?? webHindiLang, pitch: params.pitch, rate: params.rate });
    webDequeue();
  }
}

function hi(hindiStr: string, englishStr: string) {
  return (isAndroid || webHindiLang !== null) ? hindiStr : englishStr;
}

// ── Public API ────────────────────────────────────────────────────────────────

export const kitchenTTS = {
  /** KDS: new order arrived. Each item spoken as a separate utterance. */
  announceOrder(tableLabel: string, items: Array<{ name: string; qty: number }>) {
    if (!isAndroid && !hasWebSpeech) return;
    speak(hi(`नया ऑर्डर. ${tableLabel}.`, `New Order. ${tableLabel}.`));
    items.forEach(i => speak(`${i.qty} ${i.name}`));
  },

  /** POS: kitchen marked order Ready. Each item spoken one by one. */
  announceReady(orderNumber: string, items: Array<{ name: string; qty: number }>) {
    if (!isAndroid && !hasWebSpeech) return;
    speak(hi(`ऑर्डर ${orderNumber} तैयार है.`, `Order ${orderNumber} is Ready.`));
    items.forEach(i => speak(i.qty > 1 ? `${i.qty} ${i.name}` : i.name));
  },

  /** KDS: a single item within a multi-item preparing order is marked ready. */
  announceItemReady(itemName: string, qty: number) {
    if (!isAndroid && !hasWebSpeech) return;
    const prefix = qty > 1 ? `${qty} ` : '';
    speak(hi(`${prefix}${itemName} तैयार है.`, `${prefix}${itemName} is Ready.`));
  },

  cancel() {
    if (isAndroid) {
      TextToSpeech.stop().catch(() => {});
    } else if (hasWebSpeech) {
      window.speechSynthesis.cancel();
      webQueue.length = 0;
      webBusy = false;
    }
  },

  /** Speak arbitrary text with optional gender and language (used by TTS templates). */
  speakText(text: string, gender?: TTSGender, lang?: string) {
    speak(text, gender, lang);
  },

  get isSupported() { return isAndroid || hasWebSpeech; },
};
